import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Wrench, Globe2, ArrowRight, LockKeyhole, KeyRound } from "lucide-react";
import { HERO_IMAGES, LOGO_URL, FAQS, SECURITY_FAQS } from "../mock";

const About = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      {/* Hero */}
      <section className="relative pb-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGES.padlock})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/80 to-black" />

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="chip">Our story</div>
              <h1 className="mt-4 font-headline text-5xl md:text-6xl font-bold leading-[0.95] text-gradient-purple">
                We build phones that don't rat you out.
              </h1>
              <p className="mt-5 text-white/70 max-w-lg">
                Phantom Industries was started by security engineers, journalists and
                everyday paranoids who were sick of carrying tracking devices in
                their pockets. So we built our own.
              </p>
            </div>
            <div className="flex items-center justify-center">
              <img
                src={LOGO_URL}
                alt="Phantom Industries"
                className="h-96 w-auto object-contain"
                style={{
                  filter: "drop-shadow(0 0 100px rgba(168,85,247,0.6))",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            <Value icon={ShieldCheck} title="Privacy first">
              We don't collect analytics on our own site and we don't want your data. Ever.
            </Value>
            <Value icon={Wrench} title="Hand-configured">
              Every device is built, tested and inspected by a real human.
              No factory line, no shortcuts.
            </Value>
            <Value icon={Users} title="By and for the paranoid">
              Our team includes former journalists, red-teamers and criminal
              defence lawyers. We know the threat model.
            </Value>
            <Value icon={Globe2} title="Australian-owned">
              Configured in Australia, shipped worldwide in tamper-evident packaging.
            </Value>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="chip">
            <LockKeyhole className="h-3 w-3" /> Our manifesto
          </div>
          <h2 className="mt-5 font-headline text-3xl md:text-5xl font-bold text-gradient-purple">
            You should own the device in your pocket.
          </h2>
          <div className="mt-8 space-y-6 text-white/70 leading-relaxed">
            <p>
              A modern smartphone is the most invasive surveillance device the
              average person has ever carried. It knows where you are, who you
              talk to, what you buy, and what you read. And it phones home
              constantly to companies you never agreed to work for.
            </p>
            <p>
              We don't accept that as the price of a mobile phone. We use genuine
              Google Pixel hardware because it's the only major device with a
              proper open bootloader, a Titan M2 secure element and full support
              for GrapheneOS — then we methodically strip out everything Google.
            </p>
            <p>
              What's left is a phone that answers only to you. That's the whole
              product.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="chip mx-auto">Common questions</div>
            <h2 className="mt-4 font-headline text-3xl md:text-5xl font-bold text-gradient-purple">
              Frequently asked
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FaqRow key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY FAQ */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="chip mx-auto">
              <KeyRound className="h-3 w-3" /> Security & privacy
            </div>
            <h2 className="mt-4 font-headline text-3xl md:text-5xl font-bold text-gradient-purple">
              Security FAQ
            </h2>
            <p className="mt-4 text-white/60 max-w-2xl mx-auto">
              The technical questions we get most often. If it isn't covered here,
              reach us on Telegram or PGP and we'll answer directly.
            </p>
          </div>
          <div className="space-y-3">
            {SECURITY_FAQS.map((f, i) => (
              <FaqRow key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-headline text-4xl md:text-6xl font-bold text-gradient-phantom">
            Let's build yours.
          </h2>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/shop" className="btn-primary">
              Shop devices <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-ghost">Contact us</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

const Value = ({ icon: Icon, title, children }) => (
  <div className="card-glass rounded-2xl p-6 hover-lift">
    <div className="h-10 w-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
      <Icon className="h-5 w-5 text-purple-300" />
    </div>
    <div className="mt-4 font-display text-lg font-semibold text-white">{title}</div>
    <p className="mt-2 text-sm text-white/60 leading-relaxed">{children}</p>
  </div>
);

const FaqRow = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="card-glass rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="font-display text-lg text-white">{q}</span>
        <span className={`h-6 w-6 rounded-full border border-white/20 flex items-center justify-center transition-transform ${open ? "rotate-45" : ""}`}>
          <span className="h-3 w-px bg-white" />
          <span className="absolute h-px w-3 bg-white" />
        </span>
      </button>
      {open && <div className="px-5 pb-5 text-sm text-white/70 leading-relaxed">{a}</div>}
    </div>
  );
};

export default About;
